import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { CurrentShareholderGeneralMeetingModel } from '../../../models/current-shareholder-general-meeting.model';
import { RegistrationStatusEnum } from '../../../models/enums/registration-status.enum';
import { GeneralMeetingModel } from '../../../models/general-meeting.model';
import { VotingInstructionsModel } from '../../../models/voting-instructions.model';
import { GeneralMeetingCheckInService } from '../../../services/general-meeting-check-in.service';
import { GeneralMeetingsResource } from '../../../services/resources/general-meetings.resource';
import { AgendaItemsComponent } from '../../dialogs/agenda-items/agenda-items.component';

@Component({
    selector: 'sl-general-meeting-info',
    templateUrl: './general-meeting-info.component.html',
    styleUrls: ['./general-meeting-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GeneralMeetingInfoComponent extends BaseComponent implements OnInit {
    RegistrationStatusEnum = RegistrationStatusEnum;

    isLoading = false;
    currentMeeting: GeneralMeetingModel;
    currentShareholderData: CurrentShareholderGeneralMeetingModel;
    registrationStatus = RegistrationStatusEnum.NoAnswer;
    emptyBox = 'check_box_outline_blank';
    checkedBox = 'check_box';

    private readonly matDialog = inject(MatDialog);
    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly generalMeetingCheckinService = inject(GeneralMeetingCheckInService);

    ngOnInit(): void {
        const checkInSub = this.generalMeetingCheckinService.checkInStatusChange().subscribe(() => {
            this.fetchData();
        });
        this.addSubscriptions(checkInSub);

        this.fetchData();
    }

    onGiveInstructions(): void {
        let isEditing = false;

        if (
            this.registrationStatus === RegistrationStatusEnum.Representative
            || this.registrationStatus === RegistrationStatusEnum.IndependentRepresentative
        ) {
            isEditing = true;
        }

        const ref = this.matDialog.open(AgendaItemsComponent, {
            minWidth: '65%',
            minHeight: '40%',
            data: new VotingInstructionsModel({
                shareholderData: this.currentShareholderData,
                generalMeeting: this.currentMeeting,
                isEditing
            })
        });

        ref.afterClosed().subscribe(() =>
            this.generalMeetingsResource.getCurrentForShareholder().subscribe(r => {
                this.currentShareholderData = r.value;
                this.registrationStatus = r.value.registrationStatus;
            })
        );
    }

    getTemplateName(): string {
        const currentTime = new Date(new Date().getTime());

        if (this.currentMeeting && currentTime < this.currentMeeting.doorOpening) {
            return 'instructionsTempl';
        } else {
            if (this.currentShareholderData?.isCheckedIn) {
                return 'checkOutTempl';
            } else {
                return 'checkInTempl';
            }
        }
    }

    onCheckIn(): void {
        this.generalMeetingCheckinService.checkIn();
    }

    onCheckOut(): void {
        this.generalMeetingCheckinService.checkOut();
    }

    private fetchData(): void {
        this.isLoading = true;
        forkJoin({
            meeting: this.generalMeetingsResource.getCurrent(),
            shareholderData: this.generalMeetingsResource.getCurrentForShareholder()
        }).subscribe(r => {
            this.currentMeeting = r.meeting.value;
            this.currentShareholderData = r.shareholderData.value;
            this.registrationStatus = this.currentShareholderData.registrationStatus;
            this.isLoading = false;
        });
    }
}
